import React from 'react';
import Icon from '../../../components/AppIcon';

const SystemHealth = ({ healthMetrics }) => {
  const getHealthColor = (status) => {
    switch (status) {
      case 'excellent':
        return 'text-success bg-success/10 border-success/20';
      case 'good':
        return 'text-success bg-success/10 border-success/20';
      case 'fair':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'poor':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'excellent': case'good':
        return 'CheckCircle';
      case 'fair':
        return 'AlertTriangle';
      case 'poor':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">System Health</h3>
          <p className="text-sm text-muted-foreground">Real-time monitoring of registry connections</p>
        </div>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getHealthColor(healthMetrics?.overall)}`}>
          <Icon name={getHealthIcon(healthMetrics?.overall)} size={14} />
          <span className="text-xs font-medium capitalize">{healthMetrics?.overall}</span>
        </div>
      </div>
      {/* Health Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Zap" size={16} className="text-primary" />
            <span className="text-sm font-medium text-card-foreground">Uptime</span>
          </div>
          <div className="text-2xl font-bold text-card-foreground">{healthMetrics?.uptime}%</div>
          <div className="text-xs text-muted-foreground">Last 30 days</div>
        </div>

        <div className="bg-muted/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} className="text-primary" />
            <span className="text-sm font-medium text-card-foreground">Avg Response</span>
          </div>
          <div className="text-2xl font-bold text-card-foreground">{healthMetrics?.avgResponse}ms</div>
          <div className="text-xs text-muted-foreground">API response time</div>
        </div>

        <div className="bg-muted/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Database" size={16} className="text-primary" />
            <span className="text-sm font-medium text-card-foreground">Data Sync</span>
          </div>
          <div className="text-2xl font-bold text-card-foreground">{healthMetrics?.syncSuccess}%</div>
          <div className="text-xs text-muted-foreground">Success rate</div>
        </div>

        <div className="bg-muted/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-primary" />
            <span className="text-sm font-medium text-card-foreground">Error Rate</span>
          </div>
          <div className="text-2xl font-bold text-card-foreground">{healthMetrics?.errorRate}%</div>
          <div className="text-xs text-muted-foreground">Last 24 hours</div>
        </div>
      </div>
      {/* Registry Health Status */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-card-foreground">Registry Status</h4>
        {healthMetrics?.registries?.map((registry) => (
          <div key={registry?.id} className="flex items-center justify-between p-3 bg-muted/10 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name={registry?.icon} size={20} className="text-primary" />
              <div>
                <div className="text-sm font-medium text-card-foreground">{registry?.name}</div>
                <div className="text-xs text-muted-foreground">
                  Last sync: {registry?.lastSync}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-card-foreground">
                  {registry?.responseTime}ms
                </div>
                <div className="text-xs text-muted-foreground">Response time</div>
              </div>
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${getHealthColor(registry?.health)}`}>
                <Icon name={getHealthIcon(registry?.health)} size={12} />
                <span className="text-xs font-medium">{registry?.health}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* System Alerts */}
      {healthMetrics?.alerts && healthMetrics?.alerts?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="text-sm font-medium text-card-foreground mb-3">Active Alerts</h4>
          <div className="space-y-2">
            {healthMetrics?.alerts?.map((alert, index) => (
              <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg border ${
                alert?.severity === 'high' ?'bg-error/10 border-error/20' 
                  : alert?.severity === 'medium' ?'bg-warning/10 border-warning/20' :'bg-muted/10 border-border'
              }`}>
                <Icon 
                  name={alert?.severity === 'high' ? 'AlertCircle' : 'AlertTriangle'} 
                  size={16} 
                  className={alert?.severity === 'high' ? 'text-error' : 'text-warning'} 
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-card-foreground">{alert?.title}</div>
                  <div className="text-xs text-muted-foreground">{alert?.description}</div>
                  <div className="text-xs text-muted-foreground mt-1">{alert?.timestamp}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealth;